import React, { useState } from "react";
import styled from "styled-components";
import { isResponse, shortenSubject } from "../SubjectUtils";
import { lighten } from "polished";

const SERVICE_SPACING_X = 260;
const MESSAGE_SPACING_Y = 50;
const SERVICE_WIDTH = 200;
const FIRST_MESSAGE_MARGIN_TOP = 125;
const ORPHAN_MESSAGE_WIDTH = 170;

const StyledSequenceDiagram = styled.div`
  width: 100%;
  text-align: center;
  position: relative;

  .service {
    position: absolute;
    background: ${props => props.theme.colors.gray2};
    display: inline-block;
    padding: 0.5rem;
    font-size: 0.85rem;
    width: ${SERVICE_WIDTH}px;

    &::after {
      content: "";
      display: block;
      width: 2px;
      height: 1500vh; /* TODO: Make height dynamic */
      position: absolute;
      background: ${props => props.theme.colors.gray2};
      left: calc(50% - 1px);
    }
  }

  .message {
    font-size: 0.8rem;
    border-bottom: 1px solid white;
    text-align: center;
    position: absolute;
    width: ${SERVICE_SPACING_X}px;
    cursor: pointer;
    padding-bottom: 2px;

    &::after {
      content: " ";
      width: 0;
      height: 0;
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      border-left: 6px solid white;
      display: block;
      position: absolute;
      bottom: -6px;
    }

    &.ltr::after {
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      border-left: 6px solid white;
      right: -1px;
    }

    &.rtl::after {
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      border-left: 6px solid transparent;
      border-right: 6px solid white;
      left: -6px;
    }

    &.error {
      color: ${props => lighten(0.2, props.theme.colors.danger)};
    }
  }

  .message-body-panel {
    position: fixed;
    right: 0;
    top: 0;
    padding: 6rem 0.5rem 2rem 0.5rem;
    width: 400px;
    height: 100vh;
    text-align: left;
    font-size: 0.8rem;
    background: ${props => props.theme.colors.gray2};
  }
`;

interface Props {
  messages: any[];
}

const SequenceDiagram: React.FC<Props> = ({ messages }) => {
  const [showMessage, setShowMessage] = useState<any>(null);
  const serviceMap = new Map<string, any>();

  let order = 0;
  messages.forEach(message => {
    const serviceName = message.msg.from.service;

    if (!serviceMap.has(serviceName)) {
      const xPos = SERVICE_SPACING_X * order;

      serviceMap.set(serviceName, {
        name: serviceName,
        order,
        xPos,
        xPosCenter: xPos + SERVICE_WIDTH * 0.5
      });

      order++;
    }
  });

  const messages2 = messages.map((message, i) => {
    const isResp = isResponse(message.subject);

    const siblingMessage = messages.find(
      oMessage => oMessage.msg.transactionId === message.msg.transactionId && oMessage.subject !== message.subject
    );

    const to = siblingMessage ? serviceMap.get(siblingMessage.msg.from.service) : null;
    const from = serviceMap.get(message.msg.from.service);

    let direction;

    let pos = {
      top: FIRST_MESSAGE_MARGIN_TOP + MESSAGE_SPACING_Y * i,
      left: 0
    };

    if (!to || to.order > from.order) {
      direction = "ltr";
      pos.left = from.xPosCenter;
    } else {
      pos.left = to.xPosCenter;
      direction = "rtl";
    }

    return {
      isResponse: isResp,
      subject: message.subject,
      body: message.msg,
      direction,
      pos,
      from,
      to,
      width: to ? Math.abs(from.xPosCenter - to.xPosCenter) : ORPHAN_MESSAGE_WIDTH
    };
  });

  return (
    <StyledSequenceDiagram>
      {Array.from(serviceMap.values()).map(service => {
        return (
          <div className="service" key={service.name} style={{ left: service.xPos + "px" }}>
            {service.name}
          </div>
        );
      })}
      {messages2.map((message, i) => (
        <div
          key={i}
          className={`message ${message.direction} ${
            (message.isResponse && message.body.status > 399) || !message.to ? "error" : ""
          }`}
          onClick={() => {
            if (showMessage && showMessage === message.body) setShowMessage(null);
            else setShowMessage(message.body);
          }}
          style={{
            top: message.pos.top + "px",
            left: message.pos.left + "px",
            width: message.width + "px"
          }}
        >
          {shortenSubject(message.subject)}
        </div>
      ))}

      {showMessage && (
        <div className="message-body-panel">
          <pre>
            <code>{JSON.stringify(showMessage, null, 2)}</code>
          </pre>
        </div>
      )}
    </StyledSequenceDiagram>
  );
};

export default SequenceDiagram;
